<script setup>
    const emit = defineEmits(['complete', 'started', 'willComplete']);
    const canvasRef = ref(null);
    const loaded = ref(false);
    const playing = ref(false);
    const dismissing = ref(false);

    let riveInstance = null;

    onMounted(async () => {
        try {
            // Lock scroll
            document.documentElement.style.overflow = 'hidden';
            document.body.style.overflow = 'hidden';
            document.body.style.position = 'fixed';
            document.body.style.width = '100%';
            document.body.style.top = `-${window.scrollY}px`;

            await nextTick();

            const canvas = canvasRef.value;
            if (!canvas) {
                dismiss();
                return;
            }

            const dpr = window.devicePixelRatio || 1;

            function sizeCanvas() {
                canvas.width = window.innerWidth * dpr;
                canvas.height = window.innerHeight * dpr;
                canvas.style.width = window.innerWidth + 'px';
                canvas.style.height = window.innerHeight + 'px';
                if (riveInstance) riveInstance.resizeDrawingSurfaceToCanvas();
            }

            sizeCanvas();
            window.addEventListener('resize', sizeCanvas);

            const { Rive, Fit, Alignment, Layout } = await import('@rive-app/canvas');

            const loadTimeout = setTimeout(() => dismiss(), 2000);

            const isMobile = window.matchMedia('(max-width: 767px)').matches;
            const riveSrc = isMobile ? '/assets/rive/home-mobile.riv' : '/assets/rive/home.riv';

            riveInstance = new Rive({
                src: riveSrc,
                canvas,
                autoplay: false,
                layout: new Layout({ fit: Fit.Cover, alignment: Alignment.Center }),
                onLoad: () => {
                    clearTimeout(loadTimeout);
                    riveInstance.resizeDrawingSurfaceToCanvas();
                    loaded.value = true;
                    setTimeout(() => {
                        riveInstance.play();
                        if (!riveInstance.isPlaying) dismiss();
                        else {
                            requestAnimationFrame(() => {
                                playing.value = true;
                            });
                            setTimeout(() => emit('started'), 1000);
                            setTimeout(() => emit('willComplete'), 6500);
                            // Unlock scroll 1s before animation ends
                            setTimeout(unlockScroll, 4000);
                        }
                    }, 500);
                },
                onStop: () => dismiss(),
            });
        } catch {
            dismiss();
        }
    });

    onBeforeUnmount(() => {
        if (riveInstance) {
            riveInstance.cleanup();
            riveInstance = null;
        }
    });

    function unlockScroll() {
        if (document.body.style.position === 'fixed') {
            const scrollY = Math.abs(parseInt(document.body.style.top || '0'));
            document.documentElement.style.overflow = '';
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.width = '';
            document.body.style.top = '';
            window.scrollTo(0, scrollY);
        }
    }

    function dismiss() {
        if (dismissing.value) return;
        dismissing.value = true;
        unlockScroll();
        if (riveInstance) {
            riveInstance.cleanup();
            riveInstance = null;
        }
        setTimeout(() => {
            emit('complete');
        }, 0);
    }
</script>

<template>
    <div :class="['intro-overlay', playing && 'intro-bg-transparent', dismissing && 'intro-dismiss']" @click="dismiss">
        <canvas ref="canvasRef" :class="['intro-canvas', loaded && 'intro-canvas-visible']" />
    </div>
</template>

<style scoped>
    .intro-overlay {
        position: fixed;
        inset: 0;
        z-index: 9999;
        background-color: var(--teal);
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        height: 100vh;
        height: 100dvh;
        width: 100vw;
        transform: translateZ(0);
        will-change: transform, opacity;
    }

    .intro-overlay.intro-bg-transparent {
        background-color: transparent;
    }

    .intro-overlay.intro-dismiss {
        animation: intro-fade-out 0.5s linear forwards;
    }

    .intro-canvas {
        display: block;
        position: absolute;
        inset: 0;
        opacity: 0;
        transition: opacity 0.5s linear;
    }

    .intro-canvas.intro-canvas-visible {
        opacity: 1;
    }

    @keyframes intro-fade-out {
        to {
            opacity: 0;
        }
    }
</style>
